import React, { useState } from 'react';
import {
  Button,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/fa';
import { dataService } from '../services/api';

interface AddEntryProps {
  recordId: number;
  onSuccess?: () => void;
}

const AddEntry: React.FC<AddEntryProps> = ({ recordId, onSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const validateJson = (text: string): boolean => {
    try {
      JSON.parse(text);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleAdd = async () => {
    if (!validateJson(jsonInput)) {
      toast({
        title: 'Invalid JSON',
        description: 'Please enter valid JSON format',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const data = JSON.parse(jsonInput);
      await dataService.addEntry(recordId, data);
      toast({
        title: 'Entry added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsModalOpen(false);
      setJsonInput('');
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Failed to add entry',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        leftIcon={<Icon as={FaPlus} />}
        colorScheme="teal"
        onClick={() => setIsModalOpen(true)}
        size="md"
        fontWeight="semibold"
        boxShadow="sm"
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: 'md',
        }}
        transition="all 0.2s"
      >
        Add Entry
      </Button>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder="Enter your entry data in JSON format..."
              size="lg"
              height="300px"
              fontFamily="monospace"
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAdd}
              isLoading={isLoading}
            >
              Add Entry
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddEntry; 